import React, { useState, useEffect } from 'react';
import { 
  Search, Mail, Phone, MessageSquare, 
  Trash2, CheckCircle2, Loader2, MessageCircle
} from 'lucide-react';
import { db } from '../firebase';
import { collection, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';

const EnquiriesPage = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [statusMessage, setStatusMessage] = useState('');

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'enquiries'));
      const list = [];
      snap.forEach(d => {
        list.push({ id: d.id, ...d.data() });
      });
      // Sort newest first
      list.sort((a, b) => {
        const timeA = a.createdAt?.seconds || 0;
        const timeB = b.createdAt?.seconds || 0;
        return timeB - timeA;
      });
      setEnquiries(list);
    } catch (err) {
      console.error('Error fetching enquiries from Firebase:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateDoc(doc(db, 'enquiries', id), { status: newStatus });
      setEnquiries(prev => prev.map(e => e.id === id ? { ...e, status: newStatus } : e));
      setStatusMessage('Status updated');
      setTimeout(() => setStatusMessage(''), 2500);
    } catch (err) {
      alert('Error updating status: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact inquiry?')) return;
    try {
      await deleteDoc(doc(db, 'enquiries', id));
      setEnquiries(prev => prev.filter(e => e.id !== id));
    } catch (err) {
      alert('Error deleting inquiry: ' + err.message);
    }
  };

  const cleanPhoneForWhatsApp = (phoneStr) => {
    if (!phoneStr) return '';
    const cleaned = phoneStr.replace(/[^0-9]/g, '');
    if (cleaned.length === 10) return '91' + cleaned;
    return cleaned;
  };

  const filtered = enquiries.filter(item => {
    const matchesQuery = 
      (item.name || '').toLowerCase().includes(query.toLowerCase()) || 
      (item.email || '').toLowerCase().includes(query.toLowerCase()) ||
      (item.phone || '').toLowerCase().includes(query.toLowerCase()) ||
      (item.message || '').toLowerCase().includes(query.toLowerCase());
    const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
    return matchesQuery && matchesStatus;
  });

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900 flex items-center gap-2">
            <MessageSquare className="h-7 w-7 text-blue-600" /> Contact Enquiries
          </h2>
          <p className="text-xs text-slate-500 mt-1">Direct inquiries submitted through the website Contact Us page.</p>
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          {/* Status Filter */}
          <div className="flex bg-white rounded-xl border border-slate-200 p-1">
            {['All', 'New', 'Replied', 'Closed'].map(st => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                  statusFilter === st ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {st}
              </button>
            ))}
          </div>

          {/* Search bar */}
          <div className="relative flex-grow md:flex-grow-0">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
            <input 
              placeholder="Search enquiries..." 
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl outline-none text-xs w-full md:w-64 focus:border-blue-500"
            />
          </div>

          <button
            onClick={fetchEnquiries}
            className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl text-xs font-bold text-slate-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {statusMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl text-xs font-bold flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {statusMessage}
        </div>
      )}

      {/* Enquiries Table Card */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-xl overflow-hidden">
        {loading ? (
          <div className="p-20 flex flex-col items-center justify-center gap-3 text-slate-400">
            <Loader2 className="animate-spin text-blue-500 h-8 w-8" />
            <span className="text-xs font-medium">Fetching enquiries from database...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-slate-400">
            <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-30 text-blue-500" />
            <p className="font-bold text-slate-600">No contact enquiries found</p>
            <p className="text-xs mt-1">When users reach out from the Contact Us form, their messages will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50/80 border-b border-slate-100 text-slate-500 text-[11px] font-black uppercase tracking-wider">
                <tr>
                  <th className="p-4 pl-6">Contact Details</th>
                  <th className="p-4">Message</th>
                  <th className="p-4">Date</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Actions</th>
                  <th className="p-4 pr-6 text-right">Delete</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filtered.map((item) => {
                  const waNumber = cleanPhoneForWhatsApp(item.phone);
                  const waMessage = encodeURIComponent(`Hi ${item.name}, thank you for contacting GlobalEPRC regarding your enquiry.`);

                  return (
                    <tr key={item.id} className="hover:bg-slate-50/60 transition">
                      <td className="p-4 pl-6">
                        <p className="font-bold text-slate-900">{item.name}</p>
                        <div className="flex flex-col gap-0.5 mt-1 text-[11px] text-slate-500">
                          <a href={`mailto:${item.email}`} className="flex items-center gap-1.5 hover:text-blue-600">
                            <Mail className="h-3 w-3 text-slate-400" /> {item.email}
                          </a>
                          {item.phone && (
                            <a href={`tel:${item.phone}`} className="flex items-center gap-1.5 hover:text-blue-600">
                              <Phone className="h-3 w-3 text-slate-400" /> {item.phone}
                            </a>
                          )}
                        </div>
                      </td>

                      <td className="p-4 max-w-sm">
                        <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line bg-slate-50 p-3 rounded-xl border border-slate-100">
                          {item.message}
                        </p>
                      </td>

                      <td className="p-4 text-xs text-slate-500 whitespace-nowrap">
                        {item.createdAtString || (item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000).toLocaleDateString() : 'Recent')}
                      </td>

                      <td className="p-4">
                        <select
                          value={item.status || 'New'}
                          onChange={(e) => handleStatusChange(item.id, e.target.value)}
                          className={`text-xs font-bold rounded-lg px-2.5 py-1 border outline-none cursor-pointer ${
                            item.status === 'Replied' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                            item.status === 'Closed' ? 'bg-slate-100 text-slate-600 border-slate-200' :
                            'bg-blue-50 text-blue-700 border-blue-200 font-bold'
                          }`}
                        >
                          <option value="New">New</option>
                          <option value="Replied">Replied</option>
                          <option value="Closed">Closed</option>
                        </select>
                      </td>

                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {waNumber && (
                            <a
                              href={`https://wa.me/${waNumber}?text=${waMessage}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Chat on WhatsApp"
                              className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white transition"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </a>
                          )}
                          <a
                            href={`mailto:${item.email}?subject=RE: GlobalEPRC Inquiry`}
                            title="Reply by Email"
                            className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-500 hover:text-white transition"
                          >
                            <Mail className="h-4 w-4" />
                          </a>
                          {item.phone && (
                            <a
                              href={`tel:${item.phone}`}
                              title="Call"
                              className="p-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-900 hover:text-white transition"
                            >
                              <Phone className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                      </td>

                      <td className="p-4 pr-6 text-right">
                        <button
                          onClick={() => handleDelete(item.id)}
                          title="Delete Inquiry"
                          className="p-2 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnquiriesPage;

